import React, { useState, useEffect } from 'react';
import { getUsers, addUser, editUser, deleteUser } from '../../utils/api'; // Import mock API functions
import UserTable from './UserTable';
import UserFormModal from './UserFormModal';

const UserActions = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]); // State for filtered users
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false); // To track loading state
  const [successMessage, setSuccessMessage] = useState(''); // To track success message

  // Fetch users on initial load
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true); // Set loading true when fetching users
      try {
        const fetchedUsers = await getUsers();
        setUsers(fetchedUsers);
        setFilteredUsers(fetchedUsers); // Initialize filtered users with all users
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false); // Set loading false after fetching
      }
    };
    fetchUsers();
  }, []);

  // Handle add user click
  const handleAddClick = () => {
    setIsEditing(false);
    setEditingUser(null);
    setModalVisible(true);
  };

  // Handle edit user click
  const handleEditClick = (user) => {
    setIsEditing(true);
    setEditingUser(user);
    setModalVisible(true);
  };

  // Handle form submission (Add/Edit)
  const handleModalSubmit = async (user) => {
    setLoading(true); // Set loading true when submitting form
    try {
      if (isEditing) {
        await editUser(editingUser.id, user); // Update the existing user
        setSuccessMessage('User updated successfully!');
      } else {
        await addUser(user); // Add the new user
        setSuccessMessage('User added successfully!');
      }
    } catch (error) {
      console.error('Error submitting user:', error);
      setSuccessMessage('Error occurred. Please try again!');
    } finally {
      const updatedUsers = await getUsers(); // Fetch updated user list
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers); // Update filtered users
      setModalVisible(false);
      setLoading(false); // Set loading false after API call

      // Set timeout to clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  // Handle delete user
  const handleDelete = async (id) => {
    setLoading(true); // Set loading true when deleting
    try {
      await deleteUser(id);
      setSuccessMessage('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      setSuccessMessage('Error occurred. Please try again!');
    } finally {
      const updatedUsers = await getUsers(); // Fetch updated user list after deletion
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers); // Update filtered users
      setLoading(false); // Set loading false after deletion

      // Set timeout to clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  // Handle search query change
  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    // Filter users based on the query
    const filtered = users.filter(
      (user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        user.status.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Users</h3>
        <input
          type="text"
          placeholder="Search users..."
          className="form-control w-25 me-3"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <button className="btn btn-primary" onClick={handleAddClick} disabled={loading}>
          {loading ? 'Adding...' : 'Add User'}
        </button>
      </div>

      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}

      <UserTable users={filteredUsers} onEdit={handleEditClick} onDelete={handleDelete} />

      {modalVisible && (
        <UserFormModal
          onSubmit={handleModalSubmit}
          onCancel={() => setModalVisible(false)}
          user={editingUser}
          isEditing={isEditing}
        />
      )}

      {/* Loader */}
      {loading && (
        <div className="d-flex justify-content-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActions;
